import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The eyes (docs/sendas/artifice.md, chapter 4, «Los ojos»): the grounds, the same from the seed in the sim and the
 * drawing, integers only. Up is −y (the top of the screen).
 *
 * - `strutsLayout` (act 1, the memory): Grada's ledge by night in the Marea of the Thaw. The cliff's wall along the
 *   north with the quarrymen's houses cut in it (their doorsteps a row in front), the great stair broken over the ledge,
 *   hanging from four loose struts across the middle; the drop along the south, where the Marea climbs over the rim.
 * - `cartsLayout` (act 2, the carts): the dwarven mine in the glacier, a gallery three hexes wide from the foot (the way
 *   in) to the drill hall's door at the top, winding a little. Four rails cross it on their rows: each comes down a side
 *   gallery (its slope) from where a cart froze, crosses the way and stops at a buffer on the other side. Ink beetles
 *   sleep on each crossing; the brake by each cart, a row above its rail.
 * - `drillsLayout` (act 3, the test): the drill hall; a block of ice in its middle with the geode inside, its north and
 *   south faces the two walls; four drills by them (two a face), the mill at the mouth (south-west), the belts' station
 *   by it with its two levers. `drillsSet` picks the drills' forces, arrows and pairs; `drillsSolve` counts the ways.
 */

export const STRUTS_RADIUS = 7;
export const CARTS_RADIUS = 11;
export const DRILLS_RADIUS = 7;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const key = (h: Hex): string => `${h.q},${h.r}`;
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

/* ---------- the night of the promise ---------- */

export interface StrutsLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The four loose struts (on the floor: a golem stands at each with its back to it), west to east. */
  struts: Hex[];
  /** The houses cut in the wall (for the drawing) and their doorsteps (a nightmare there is in). */
  houses: Hex[];
  doors: { x: number; y: number }[];
  /** Where the Marea comes over the rim (the drop's edge). */
  rim: { x: number; y: number }[];
  /** The order the struts creak in (the loosest first): Grillo's lamp shows the first not yet wedged. */
  creak: number[];
  golemsAt: { x: number; y: number }[];
  bruniAt: { x: number; y: number };
  grilloAt: { x: number; y: number };
  quarryAt: { x: number; y: number }[];
  start: { x: number; y: number };
}

export const strutsLayout = (seed: number): StrutsLayout => {
  const R = STRUTS_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x5791));
  // The houses' row in the wall (r = −5) and their doorsteps in front (r = −4); the ledge from −4 to the rim.
  const houses: Hex[] = [-2, 0, 2, 4, 6].map((q) => ({ q, r: -5 }));
  const floor = allCells(R).filter((h) => h.r >= -4 && hexLen(h.q, h.r) <= R - 1);
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const struts: Hex[] = [-2, 0, 2, 4].map((q) => ({ q, r: -2 }));
  const doors = houses.map((h) => hexToWorld(h.q, h.r + 1));
  const rim = floor.filter((h) => h.r >= R - 2 && hexLen(h.q, h.r) === R - 1).sort(sortHex).map((h) => hexToWorld(h.q, h.r));
  // The creak order: a shuffle of the four from the seed.
  const creak = [0, 1, 2, 3];
  for (let i = creak.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    const t = creak[i] as number;
    creak[i] = creak[j] as number;
    creak[j] = t;
  }
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    struts,
    houses,
    doors,
    rim,
    creak,
    golemsAt: [hexToWorld(-1, -3), hexToWorld(1, -3)],
    bruniAt: hexToWorld(0, -3),
    grilloAt: hexToWorld(-1, 0),
    quarryAt: [hexToWorld(-2, -4), hexToWorld(2, -4)],
    start: hexToWorld(0, 0),
  };
};

/* ---------- the carts ---------- */

export interface CartRail {
  /** The rail's row (r), its cells from the cart's top to the buffer, in world points. */
  row: number;
  cells: Hex[];
  from: { x: number; y: number };
  to: { x: number; y: number };
  /** The brake's cell (a row above the cart), the gallery's cells on the crossing (shut while a shell sleeps there). */
  brake: Hex;
  gate: Hex[];
  /** Which side the slope comes down from (−1 west, 1 east). */
  side: number;
  /** The beetles asleep on the crossing. */
  beetles: { x: number; y: number }[];
}

export interface CartsLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The gallery's middle, row by row from the foot (the way in) to the top. */
  way: { x: number; y: number }[];
  rails: CartRail[];
  /** Where shades come down from: a hole in the gallery's wall in each stretch (between one crossing and the next). */
  holes: { x: number; y: number; stretch: number }[];
  door: { x: number; y: number };
  start: { x: number; y: number };
}

export const cartsLayout = (seed: number): CartsLayout => {
  const R = CARTS_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xca27));
  // The gallery's middle on each row (q at the row, as a world x): it winds, a half hex every other row.
  const mid = new Map<number, number>();
  let x = 0;
  for (let r = R - 2; r >= -(R - 2); r--) {
    mid.set(r, x);
    if (r % 2 === 0) {
      const d = rngInt(rng, 3) - 1;
      x = imax(-1500, imin(1500, x + d * 500));
    }
  }
  const inGallery = (h: Hex): boolean => {
    const c = hexToWorld(h.q, h.r);
    const m = mid.get(h.r);
    return m !== undefined && c.x - m <= 1600 && m - c.x <= 1600 && hexLen(h.q, h.r) <= R;
  };
  const cells = new Map<string, Hex>();
  for (const h of allCells(R)) if (inGallery(h)) cells.set(key(h), h);
  // The four crossings: rows 6, 2, −2 and −6 from the foot; each slope from alternating sides (the first from the seed).
  const rows = [6, 2, -2, -6];
  const first = rngInt(rng, 2) === 0 ? -1 : 1;
  const rails: CartRail[] = rows.map((row, i) => {
    const side = i % 2 === 0 ? first : -first;
    const gate = allCells(R).filter((h) => h.r === row && inGallery(h)).sort((a, b) => a.q - b.q);
    const edge = side < 0 ? (gate[0] as Hex) : (gate[gate.length - 1] as Hex);
    const far = side < 0 ? (gate[gate.length - 1] as Hex) : (gate[0] as Hex);
    // The slope: three cells out from the gallery on its side (the cart at the end). The brake a row below its top, on
    // the hero's side, at the end of a ledge out from the gallery along that row (its way there: never the rail, and
    // never round the crossing: the rail's row is shut whole while a shell sleeps on it).
    const slope: Hex[] = [];
    for (let k = 1; k <= 3; k++) slope.push({ q: edge.q + side * k, r: row });
    const top = slope[slope.length - 1] as Hex;
    const brake: Hex = { q: top.q + (side < 0 ? 0 : -1), r: row + 1 };
    const buffer: Hex = { q: far.q - side, r: row };
    for (const h of [...slope, brake, buffer]) cells.set(key(h), h);
    const below = allCells(R).filter((h) => h.r === row + 1 && inGallery(h)).sort((a, b) => a.q - b.q);
    const from = side < 0 ? (below[0] as Hex) : (below[below.length - 1] as Hex);
    for (let q = from.q + side; side < 0 ? q > brake.q : q < brake.q; q += side) cells.set(key({ q, r: row + 1 }), { q, r: row + 1 });
    const line = [...slope.slice().reverse(), ...(side < 0 ? gate : gate.slice().reverse()), buffer];
    const n = i === 0 ? 1 : 2;
    const beds = side < 0 ? gate : gate.slice().reverse();
    const beetles = Array.from({ length: n }, (_, k) => {
      const h = beds[imin(beds.length - 1, 1 + k)] as Hex;
      return hexToWorld(h.q, h.r);
    });
    return { row, cells: line, from: hexToWorld(top.q, top.r), to: hexToWorld(buffer.q, buffer.r), brake, gate, side, beetles };
  });
  const floor = [...cells.values()].filter((h) => hexLen(h.q, h.r) <= R).sort(sortHex);
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  const way: { x: number; y: number }[] = [];
  for (let r = R - 2; r >= -(R - 2); r--) way.push({ x: mid.get(r) as number, y: hexToWorld(0, r).y });
  // A hole in the wall in each stretch above a crossing (where shades come down), on the side away from its slope.
  const holes = rails.map((rl, i) => {
    const r = rl.row - 2;
    const m = mid.get(r) as number;
    return { x: m - rl.side * 1600, y: hexToWorld(0, r).y, stretch: i + 1 };
  });
  const top = R - 2;
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor,
    way,
    rails,
    holes,
    door: { x: mid.get(-top) as number, y: hexToWorld(0, -top).y },
    start: { x: mid.get(top) as number, y: hexToWorld(0, top).y },
  };
};

/* ---------- the drills ---------- */

/** Drills in the hall; their walls (0: north, 1: south); belts (0: I, drills 1 and 2; 1: II, drills 3 and 4). */
export const DRILLS = 4;
export const beltOf = (d: number): number => (d < 2 ? 0 : 1);

export interface DrillsSet {
  /** Each drill's force (1 or 2), its wall, and whether it turns its arrow's way with both belts as they start. */
  force: number[];
  wall: number[];
  right0: boolean[];
}

/** Whether drill `d` turns its arrow's way with the belts turned round as `belts` says (1: turned round). */
export const drillRight = (set: DrillsSet, belts: number[], d: number): boolean => (set.right0[d] as boolean) !== ((belts[beltOf(d)] ?? 0) === 1);

/**
 * The drills from the seed: each wall has a drill of each belt, one of force 1 and one of 2 (3, the mill's whole); the
 * two drills of a belt are on different walls with opposite arrows, so a belt never leaves both right; and each wall
 * wants one belt turned from the start (three levers at the fewest, either wall first: two ways).
 */
export const drillsSet = (seed: number): DrillsSet => {
  const rng = seedRng(deriveSeed(seed, 0xd211));
  // The pairs: north takes drill 1 and drill 3 or 4; south the other two.
  const p = rngInt(rng, 2);
  const wall = [0, 1, p === 0 ? 0 : 1, p === 0 ? 1 : 0];
  // The forces: on each wall, one of 1 and one of 2.
  const force = [0, 0, 0, 0];
  for (const w of [0, 1]) {
    const ds = [0, 1, 2, 3].filter((d) => wall[d] === w);
    const big = rngInt(rng, 2);
    force[ds[0] as number] = big === 0 ? 2 : 1;
    force[ds[1] as number] = big === 0 ? 1 : 2;
  }
  // The arrows: north needs exactly one belt turned (which, from the seed); south needs the other one (both differ).
  const turn = rngInt(rng, 2);
  const right0 = [false, false, false, false];
  for (let d = 0; d < DRILLS; d++) {
    const north = wall[d] === 0;
    const needTurn = north ? beltOf(d) === turn : beltOf(d) !== turn;
    right0[d] = !needTurn;
  }
  return { force, wall, right0 };
};

/**
 * The fewest lever pulls to bring both walls down (each wall cut with its two drills right and nothing else in: the
 * mill's 3 are its two), and how many orders of the walls do it in that many. Clutches cost nothing here (any number).
 */
export const drillsSolve = (set: DrillsSet): { pulls: number; ways: number } => {
  const need = [0, 1].map((w) => [0, 1].map((b) => {
    const d = [0, 1, 2, 3].find((x) => set.wall[x] === w && beltOf(x) === b) as number;
    return set.right0[d] ? 0 : 1;
  }));
  const ham = (a: number[], b: number[]): number => (a[0] !== b[0] ? 1 : 0) + (a[1] !== b[1] ? 1 : 0);
  const start = [0, 0];
  const orders = [
    ham(start, need[0] as number[]) + ham(need[0] as number[], need[1] as number[]),
    ham(start, need[1] as number[]) + ham(need[1] as number[], need[0] as number[]),
  ];
  const pulls = imin(orders[0] as number, orders[1] as number);
  return { pulls, ways: orders.filter((o) => o === pulls).length };
};

export interface DrillsLayout {
  radius: number;
  rocks: Hex[];
  floor: Hex[];
  /** The ice block with the geode (solid), its north and south faces' cells (the walls, for the drawing). */
  block: Hex[];
  walls: Hex[][];
  /** Each drill's cell (solid: the machine; worked from beside it), west to east, 1 and 2 then 3 and 4. */
  drills: Hex[];
  /** Where each clutch is worked from (the floor beside its drill, away from the block). */
  spots: Hex[];
  /** The belts' levers (I, II), the mill (solid, at the mouth). */
  levers: Hex[];
  mill: Hex;
  /** Where La Barrena puts itself together (the broken drills' heap), where the frozen ones come out of the ice. */
  heap: { x: number; y: number };
  ice: { x: number; y: number }[];
  dunnaAt: { x: number; y: number };
  start: { x: number; y: number };
  set: DrillsSet;
}

export const drillsLayout = (seed: number): DrillsLayout => {
  const R = DRILLS_RADIUS;
  const set = drillsSet(seed);
  // The block: three wide, two deep, its middle a little north of the hall's.
  const block: Hex[] = [
    { q: -1, r: -1 },
    { q: 0, r: -1 },
    { q: 1, r: -1 },
    { q: -1, r: 0 },
    { q: 0, r: 0 },
  ];
  const walls: Hex[][] = [block.filter((h) => h.r === -1), block.filter((h) => h.r === 0)];
  // The drills: two by the north face (row −2) and two by the south face (row 1); which drill where by its wall.
  const northAt: Hex[] = [
    { q: -1, r: -2 },
    { q: 1, r: -2 },
  ];
  const southAt: Hex[] = [
    { q: -2, r: 1 },
    { q: 0, r: 1 },
  ];
  const drills: Hex[] = [];
  let n = 0;
  let sth = 0;
  for (let d = 0; d < DRILLS; d++) drills.push(set.wall[d] === 0 ? (northAt[n++] as Hex) : (southAt[sth++] as Hex));
  const levers: Hex[] = [
    { q: -4, r: 3 },
    { q: -3, r: 3 },
  ];
  const mill: Hex = { q: -5, r: 4 };
  const solid = new Set([...block, ...drills, mill].map(key));
  const floor = allCells(R).filter((h) => hexLen(h.q, h.r) <= R - 1 && !solid.has(key(h)) && h.r >= -5);
  const open = new Set(floor.map(key));
  const rocks = allCells(R + 2).filter((h) => !open.has(key(h)));
  return {
    radius: R,
    rocks: rocks.sort(sortHex),
    floor: floor.sort(sortHex),
    block,
    walls,
    drills,
    spots: drills.map((h, d) => (set.wall[d] === 0 ? { q: h.q, r: h.r - 1 } : { q: h.q, r: h.r + 1 })),
    levers,
    mill,
    heap: hexToWorld(5, -3),
    ice: [hexToWorld(-3, -1), hexToWorld(3, -1), hexToWorld(2, 0)],
    dunnaAt: hexToWorld(-1, 4),
    start: hexToWorld(1, 4),
    set,
  };
};
